import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Chip,
  Tabs,
  Tab,
  Checkbox,
  FormGroup,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import GroupIcon from '@mui/icons-material/Groups';
import ShareIcon from '@mui/icons-material/FolderShared';
import { useTranslation } from 'react-i18next';
import api from '../config/api';
import { DOCUMENT, documentAddButtonSx, documentTextButtonSx } from '../constants/documentTheme';
import DocumentSectionHead from './DocumentSectionHead';
import DocumentPreviewDialog from './DocumentPreviewDialog';
import { getPreviewKind, getPreviewMimeType } from '../utils/documentPreview';

/**
 * The document vault: passports, visas, tickets and insurance for a trip.
 *
 * On partner group trips, `audiences` enables Shared / Personal / Sub-Groups
 * sections — the same shape as Client itinerary — so the partner can target
 * one traveler or a named subgroup while shared vault docs stay on the trip.
 */

const CATEGORY_KEYS = [
  'passport',
  'visa',
  'ticket',
  'boarding_pass',
  'insurance',
  'reservation',
  'other'
];

const ACCEPTED_TYPES = '.pdf,.jpg,.jpeg,.png,.webp,.heic,.heif,.doc,.docx,.txt';

const AUDIENCE_TABS = [
  { value: 'shared', icon: ShareIcon, labelKey: 'documents.tabShared' },
  { value: 'personal', icon: PersonIcon, labelKey: 'documents.tabPersonal' },
  { value: 'subgroup', icon: GroupIcon, labelKey: 'documents.tabSubgroup' },
];

const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const documentAudience = (document_) => {
  if (document_?.audience) return document_.audience;
  if (document_?.for_traveler_name) return 'personal';
  return 'shared';
};

const TripDocuments = ({
  tripId,
  currentUserId,
  isCreator = false,
  isPartner = false,
  isActive = true,
  audiences = [],
  /** Deep-link target from ?doc= — opens that file once documents load. */
  focusDocumentId = null,
  onFocusDocumentHandled = null,
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const showAudienceTabs = audiences.length > 0;
  const [documents, setDocuments] = useState([]);
  const [storageAvailable, setStorageAvailable] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [audienceTab, setAudienceTab] = useState('shared');
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [openingId, setOpeningId] = useState(null);
  const [preview, setPreview] = useState(null);
  const previewUrlRef = useRef(null);
  const [form, setForm] = useState({
    file: null,
    title: '',
    category: 'other',
    notes: '',
    shareWithTrip: Boolean(isPartner),
    expiresOn: '',
    audience: 'shared',
    audienceTripId: '',
    subgroupName: '',
    participantTripIds: [],
  });

  const errorText = error?.key ? t(error.key) : error?.message;

  const fetchDocuments = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/trips/${tripId}/documents`, isPartner ? { usePartnerAuth: true } : {});
      setDocuments(response.data.documents || []);
      setStorageAvailable(response.data.storageAvailable !== false);
    } catch (err) {
      console.error('Error fetching documents:', err);
      setError({ key: 'documents.loadFailed' });
    } finally {
      setLoading(false);
    }
  }, [tripId, isPartner]);

  useEffect(() => {
    if (isActive) {
      fetchDocuments();
    }
  }, [isActive, fetchDocuments]);

  const filteredDocuments = useMemo(() => {
    if (!showAudienceTabs) return documents;
    return documents.filter((doc) => documentAudience(doc) === audienceTab);
  }, [documents, showAudienceTabs, audienceTab]);

  const resetForm = (forTab = audienceTab) => {
    setForm({
      file: null,
      title: '',
      category: 'other',
      notes: '',
      shareWithTrip: forTab === 'shared' ? Boolean(isPartner) : false,
      expiresOn: '',
      audience: showAudienceTabs ? forTab : 'shared',
      audienceTripId: '',
      subgroupName: '',
      participantTripIds: [],
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const openUploadDialog = () => {
    resetForm(showAudienceTabs ? audienceTab : 'shared');
    setUploadDialogOpen(true);
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0] || null;
    setForm((prev) => ({
      ...prev,
      file,
      title: prev.title || (file ? file.name.replace(/\.[^.]+$/, '') : '')
    }));
  };

  const handleUpload = async () => {
    if (!form.file) {
      setError({ key: 'documents.fileRequired' });
      return;
    }

    const audience = showAudienceTabs ? form.audience : (form.shareWithTrip ? 'shared' : 'personal');
    if (audience === 'personal' && showAudienceTabs && !form.audienceTripId) {
      setError({ key: 'documents.travelerRequired' });
      return;
    }
    if (audience === 'subgroup') {
      if (!form.subgroupName.trim()) {
        setError({ key: 'documents.subgroupNameRequired' });
        return;
      }
      if (form.participantTripIds.length === 0) {
        setError({ key: 'documents.participantsRequired' });
        return;
      }
    }

    setUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('document', form.file);
      formData.append('title', form.title || form.file.name);
      formData.append('category', form.category);
      formData.append('audience', audience);
      formData.append(
        'visibility',
        ((audience === 'shared' && form.shareWithTrip)
          || (!showAudienceTabs && form.shareWithTrip)) ? 'trip' : 'private'
      );
      if (form.notes) formData.append('notes', form.notes);
      if (form.expiresOn) formData.append('expiresOn', form.expiresOn);
      if (audience === 'personal' && form.audienceTripId) {
        formData.append('audienceTripId', form.audienceTripId);
      }
      if (audience === 'subgroup') {
        formData.append('subgroupName', form.subgroupName.trim());
        formData.append('participantTripIds', JSON.stringify(form.participantTripIds));
      }

      await api.post(`/trips/${tripId}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
        usePartnerAuth: isPartner
      });

      await fetchDocuments();
      setUploadDialogOpen(false);
      resetForm();
    } catch (err) {
      setError(err.response?.data?.error
        ? { message: err.response.data.error }
        : { key: 'documents.uploadFailed' });
    } finally {
      setUploading(false);
    }
  };

  const fetchDocumentBlob = (document_) => api.get(`/trips/${tripId}/documents/${document_.id}/download`, {
    responseType: 'blob',
    usePartnerAuth: isPartner
  }).then((response) => response.data);

  const saveObjectUrl = (url, document_) => {
    const link = window.document.createElement('a');
    link.href = url;
    link.download = document_.file_name || document_.title;
    window.document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const handleDownload = async (document_) => {
    setDownloadingId(document_.id);
    setError(null);

    try {
      const blob = await fetchDocumentBlob(document_);
      const url = window.URL.createObjectURL(blob);
      saveObjectUrl(url, document_);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.data?.error
        ? { message: err.response.data.error }
        : { key: 'documents.downloadFailed' });
    } finally {
      setDownloadingId(null);
    }
  };

  const closePreview = () => {
    if (preview?.url) window.URL.revokeObjectURL(preview.url);
    setPreview(null);
  };

  useEffect(() => {
    previewUrlRef.current = preview?.url || null;
  }, [preview]);

  useEffect(() => () => {
    if (previewUrlRef.current) window.URL.revokeObjectURL(previewUrlRef.current);
  }, []);

  const handleOpen = async (document_) => {
    const previewMimeType = getPreviewMimeType(document_);
    if (!previewMimeType) {
      setPreview({ document: document_, url: null, kind: null });
      return;
    }

    setOpeningId(document_.id);
    setError(null);

    try {
      const blob = await fetchDocumentBlob(document_);
      const typedBlob = new Blob([blob], { type: previewMimeType });
      setPreview({
        document: document_,
        url: window.URL.createObjectURL(typedBlob),
        kind: getPreviewKind(document_),
      });
    } catch (err) {
      setError(err.response?.data?.error
        ? { message: err.response.data.error }
        : { key: 'documents.openFailed' });
    } finally {
      setOpeningId(null);
    }
  };

  const documentDeepLink = (documentId) => {
    if (isPartner) {
      return `/partner/trips/${tripId}?tab=documents&doc=${documentId}`;
    }
    return `/trips/${tripId}?tab=documents&doc=${documentId}`;
  };

  // Open a deep-linked document once the vault has loaded.
  useEffect(() => {
    if (!isActive || loading || !focusDocumentId || !documents.length) return undefined;
    const target = documents.find((doc) => String(doc.id) === String(focusDocumentId));
    if (!target) {
      onFocusDocumentHandled?.();
      return undefined;
    }
    let cancelled = false;
    (async () => {
      await handleOpen(target);
      if (!cancelled) onFocusDocumentHandled?.();
    })();
    return () => {
      cancelled = true;
    };
    // Intentionally run when the focus id / loaded docs change — not on every handleOpen identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, loading, focusDocumentId, documents]);

  const handlePreviewDownload = () => {
    if (!preview) return;
    if (preview.url) {
      saveObjectUrl(preview.url, preview.document);
      return;
    }
    handleDownload(preview.document);
  };

  const handleToggleVisibility = async (document_) => {
    if (documentAudience(document_) !== 'shared') return;
    setError(null);
    try {
      await api.patch(`/trips/${tripId}/documents/${document_.id}`, {
        visibility: document_.visibility === 'trip' ? 'private' : 'trip'
      }, { usePartnerAuth: isPartner });
      await fetchDocuments();
    } catch (err) {
      setError(err.response?.data?.error
        ? { message: err.response.data.error }
        : { key: 'documents.updateFailed' });
    }
  };

  const handleDelete = async (document_) => {
    if (!window.confirm(t('documents.confirmDelete'))) return;

    setError(null);
    try {
      await api.delete(`/trips/${tripId}/documents/${document_.id}`, { usePartnerAuth: isPartner });
      await fetchDocuments();
    } catch (err) {
      setError(err.response?.data?.error
        ? { message: err.response.data.error }
        : { key: 'documents.deleteFailed' });
    }
  };

  const toggleParticipant = (travelerTripId) => {
    setForm((prev) => {
      const id = String(travelerTripId);
      const selected = prev.participantTripIds.map(String);
      const next = selected.includes(id)
        ? selected.filter((value) => value !== id)
        : [...selected, id];
      return { ...prev, participantTripIds: next };
    });
  };

  const audienceChipLabel = (document_) => {
    const audience = documentAudience(document_);
    if (audience === 'personal') {
      return document_.for_traveler_name
        ? t('documents.forTraveler', { name: document_.for_traveler_name })
        : t('documents.tabPersonal');
    }
    if (audience === 'subgroup') {
      return document_.subgroup_name
        ? t('documents.subgroupChip', { name: document_.subgroup_name })
        : t('documents.tabSubgroup');
    }
    return document_.visibility === 'trip' ? t('documents.sharedWithTrip') : t('documents.private');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <CircularProgress />
      </Box>
    );
  }

  const emptyHintKey = showAudienceTabs
    ? (audienceTab === 'personal'
      ? 'documents.emptyPersonalHint'
      : audienceTab === 'subgroup'
        ? 'documents.emptySubgroupHint'
        : 'documents.emptySharedHint')
    : 'documents.emptyHint';

  return (
    <Box>
      <DocumentSectionHead
        title={t('documents.title')}
        meta={t('documents.count', { count: showAudienceTabs ? filteredDocuments.length : documents.length })}
      />

      <Typography sx={{ color: DOCUMENT.ink3, fontSize: 14, mb: 2.5 }}>
        {showAudienceTabs ? t('documents.explainerGroup') : t('documents.explainer')}
      </Typography>

      {showAudienceTabs && (
        <Tabs
          value={audienceTab}
          onChange={(_, value) => setAudienceTab(value)}
          sx={{
            mb: 2,
            minHeight: 44,
            borderBottom: `1px solid ${DOCUMENT.rule}`,
            '& .MuiTab-root': {
              minHeight: 44,
              textTransform: 'none',
              fontSize: 14,
              color: DOCUMENT.ink3,
              '&.Mui-selected': { color: DOCUMENT.oxblood },
            },
            '& .MuiTabs-indicator': { bgcolor: DOCUMENT.oxblood, height: 3 },
          }}
          data-testid="document-audience-tabs"
        >
          {AUDIENCE_TABS.map(({ value, icon: Icon, labelKey }) => (
            <Tab
              key={value}
              value={value}
              icon={<Icon fontSize="small" />}
              iconPosition="start"
              label={t(labelKey)}
            />
          ))}
        </Tabs>
      )}

      {!storageAvailable && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('documents.storageUnavailable')}
        </Alert>
      )}

      {errorText && !uploadDialogOpen && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorText}
        </Alert>
      )}

      {filteredDocuments.length === 0 ? (
        <Box sx={{ py: 2 }}>
          <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 18, mb: 1 }}>
            {t('documents.empty')}
          </Typography>
          <Typography sx={{ color: DOCUMENT.ink3, mb: 2, fontSize: 14 }}>
            {t(emptyHintKey)}
          </Typography>
          <Button onClick={openUploadDialog} sx={documentAddButtonSx}>
            {t('documents.addFirstDocument')}
          </Button>
        </Box>
      ) : (
        <Box>
          {filteredDocuments.map((document_) => {
            const isOwner = document_.uploaded_by === currentUserId;
            const audience = documentAudience(document_);
            const isShared = audience === 'shared' && document_.visibility === 'trip';
            return (
              <Box
                key={document_.id}
                data-testid="trip-document"
                data-audience={audience}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '1.5fr 0.8fr 0.8fr auto' },
                  gap: 1.5,
                  py: 1.75,
                  borderBottom: `1px solid ${DOCUMENT.rule}`,
                  fontSize: 14,
                  alignItems: 'baseline',
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    component="a"
                    href={documentDeepLink(document_.id)}
                    onClick={(event) => {
                      event.preventDefault();
                      if (openingId !== document_.id) handleOpen(document_);
                    }}
                    data-testid="trip-document-title"
                    aria-disabled={openingId === document_.id}
                    sx={{
                      display: 'inline',
                      fontWeight: 500,
                      p: 0,
                      border: 0,
                      bgcolor: 'transparent',
                      color: DOCUMENT.ink,
                      font: 'inherit',
                      textAlign: 'left',
                      textDecoration: 'none',
                      cursor: openingId === document_.id ? 'progress' : 'pointer',
                      overflowWrap: 'anywhere',
                      '&:hover, &:focus-visible': {
                        textDecoration: 'underline',
                        color: DOCUMENT.oxblood,
                      },
                    }}
                  >
                    {document_.title}
                  </Typography>
                  <Typography sx={{ fontSize: 12.5, color: DOCUMENT.ink3, mt: 0.25 }}>
                    {document_.file_name ? (
                      <Box
                        component="a"
                        href={documentDeepLink(document_.id)}
                        onClick={(event) => {
                          event.preventDefault();
                          if (openingId !== document_.id) handleOpen(document_);
                        }}
                        data-testid="trip-document-filename"
                        sx={{
                          color: 'inherit',
                          textDecoration: 'none',
                          '&:hover, &:focus-visible': {
                            textDecoration: 'underline',
                            color: DOCUMENT.oxblood,
                          },
                        }}
                      >
                        {document_.file_name}
                      </Box>
                    ) : null}
                    {document_.file_name && document_.file_size ? ' · ' : ''}
                    {formatFileSize(document_.file_size)}
                  </Typography>
                  {document_.notes && (
                    <Typography sx={{ fontSize: 12.5, color: DOCUMENT.ink3, mt: 0.5 }}>
                      {document_.notes}
                    </Typography>
                  )}
                  {audience === 'subgroup' && document_.participants?.length > 0 && (
                    <Typography
                      data-testid="trip-document-participants"
                      sx={{ fontSize: 12.5, color: DOCUMENT.ink3, mt: 0.5 }}
                    >
                      {t('documents.participantsList', {
                        names: document_.participants
                          .map((p) => p.traveler_name)
                          .filter(Boolean)
                          .join(', '),
                      })}
                    </Typography>
                  )}
                </Box>

                <Typography
                  sx={{ fontSize: 10, letterSpacing: '0.16em', textTransform: 'uppercase', color: DOCUMENT.ink3 }}
                >
                  {t(`documents.category.${document_.category}`)}
                </Typography>

                <Box>
                  <Chip
                    size="small"
                    label={audienceChipLabel(document_)}
                    sx={{
                      borderRadius: 0,
                      fontSize: 10,
                      letterSpacing: '0.12em',
                      textTransform: 'uppercase',
                      bgcolor: isShared || audience !== 'shared' ? DOCUMENT.rule : 'transparent',
                      border: `1px solid ${isShared || audience !== 'shared' ? DOCUMENT.ruleHard : DOCUMENT.rule}`,
                    }}
                  />
                  {!isOwner && (
                    <Typography sx={{ fontSize: 12.5, color: DOCUMENT.ink3, mt: 0.5 }}>
                      {document_.uploaded_by_first_name || document_.uploaded_by_username}
                    </Typography>
                  )}
                  {document_.expires_on && (
                    <Typography sx={{ fontSize: 12.5, color: DOCUMENT.ink3, mt: 0.5 }}>
                      {t('documents.expires', { date: new Date(document_.expires_on).toLocaleDateString() })}
                    </Typography>
                  )}
                </Box>

                <Box sx={{ display: 'flex', gap: 1, justifyContent: { md: 'flex-end' }, flexWrap: 'wrap' }}>
                  <Button
                    size="small"
                    disabled={openingId === document_.id}
                    onClick={() => handleOpen(document_)}
                    sx={documentTextButtonSx}
                  >
                    {openingId === document_.id ? t('documents.opening') : t('documents.open')}
                  </Button>
                  <Button
                    size="small"
                    disabled={downloadingId === document_.id}
                    onClick={() => handleDownload(document_)}
                    sx={documentTextButtonSx}
                  >
                    {downloadingId === document_.id ? t('documents.downloading') : t('documents.download')}
                  </Button>
                  {isOwner && audience === 'shared' && (
                    <Button
                      size="small"
                      onClick={() => handleToggleVisibility(document_)}
                      sx={documentTextButtonSx}
                    >
                      {isShared ? t('documents.makePrivate') : t('documents.shareWithTrip')}
                    </Button>
                  )}
                  {(isOwner || isPartner || (isCreator && String(document_.trip_id) === String(tripId))) && (
                    <Button
                      size="small"
                      onClick={() => handleDelete(document_)}
                      sx={{ ...documentTextButtonSx, '&:hover': { color: '#8C2B22', borderBottom: '1px solid #8C2B22' } }}
                    >
                      {t('common.delete')}
                    </Button>
                  )}
                </Box>
              </Box>
            );
          })}
          <Button onClick={openUploadDialog} sx={documentAddButtonSx}>
            {t('documents.addDocument')}
          </Button>
        </Box>
      )}

      <Dialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        disableScrollLock
      >
        <DialogTitle>{t('documents.addDocument')}</DialogTitle>
        <DialogContent>
          {errorText && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorText}
            </Alert>
          )}

          <Button
            component="label"
            fullWidth
            sx={{
              ...documentAddButtonSx,
              justifyContent: 'center',
              mt: 1,
              mb: 2,
            }}
          >
            {form.file ? form.file.name : t('documents.chooseFile')}
            <input
              ref={fileInputRef}
              type="file"
              hidden
              accept={ACCEPTED_TYPES}
              onChange={handleFileChange}
            />
          </Button>

          {showAudienceTabs && (
            <FormControl fullWidth margin="normal">
              <InputLabel id="document-audience-type-label">{t('documents.audienceTypeLabel')}</InputLabel>
              <Select
                labelId="document-audience-type-label"
                value={form.audience}
                label={t('documents.audienceTypeLabel')}
                data-testid="document-audience-type-select"
                onChange={(e) => setForm({
                  ...form,
                  audience: e.target.value,
                  audienceTripId: '',
                  subgroupName: '',
                  participantTripIds: [],
                  shareWithTrip: e.target.value === 'shared',
                })}
              >
                <MenuItem value="shared">{t('documents.tabShared')}</MenuItem>
                <MenuItem value="personal">{t('documents.tabPersonal')}</MenuItem>
                <MenuItem value="subgroup">{t('documents.tabSubgroup')}</MenuItem>
              </Select>
            </FormControl>
          )}

          {showAudienceTabs && form.audience === 'personal' && (
            <FormControl fullWidth margin="normal">
              <InputLabel id="document-audience-label">{t('documents.audienceLabel')}</InputLabel>
              <Select
                labelId="document-audience-label"
                value={form.audienceTripId}
                label={t('documents.audienceLabel')}
                displayEmpty
                data-testid="document-audience-select"
                onChange={(e) => setForm({ ...form, audienceTripId: e.target.value })}
              >
                <MenuItem value="">{t('documents.audiencePickTraveler')}</MenuItem>
                {audiences.map((audience) => (
                  <MenuItem key={audience.tripId} value={String(audience.tripId)}>
                    {audience.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {showAudienceTabs && form.audience === 'subgroup' && (
            <Box sx={{ mt: 1 }}>
              <TextField
                fullWidth
                margin="normal"
                label={t('documents.subgroupName')}
                value={form.subgroupName}
                data-testid="document-subgroup-name"
                onChange={(e) => setForm({ ...form, subgroupName: e.target.value })}
              />
              <Typography sx={{ fontSize: 12.5, color: DOCUMENT.ink3, mt: 1, mb: 0.5 }}>
                {t('documents.selectParticipants')}
              </Typography>
              <FormGroup data-testid="document-participant-list">
                {audiences.map((audience) => (
                  <FormControlLabel
                    key={audience.tripId}
                    control={(
                      <Checkbox
                        size="small"
                        checked={form.participantTripIds.map(String).includes(String(audience.tripId))}
                        onChange={() => toggleParticipant(audience.tripId)}
                        sx={{ color: DOCUMENT.ink3, '&.Mui-checked': { color: DOCUMENT.oxblood } }}
                      />
                    )}
                    label={audience.label}
                  />
                ))}
              </FormGroup>
            </Box>
          )}

          {!showAudienceTabs && audiences.length > 0 && (
            <FormControl fullWidth margin="normal">
              <InputLabel id="document-audience-label">{t('documents.audienceLabel')}</InputLabel>
              <Select
                labelId="document-audience-label"
                value={form.audienceTripId}
                label={t('documents.audienceLabel')}
                displayEmpty
                data-testid="document-audience-select"
                onChange={(e) => setForm({ ...form, audienceTripId: e.target.value })}
              >
                <MenuItem value="">{t('documents.audienceEveryone')}</MenuItem>
                {audiences.map((audience) => (
                  <MenuItem key={audience.tripId} value={String(audience.tripId)}>
                    {audience.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <TextField
            fullWidth
            margin="normal"
            label={t('documents.documentTitle')}
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
          />

          <FormControl fullWidth margin="normal">
            <InputLabel id="document-category-label">{t('documents.categoryLabel')}</InputLabel>
            <Select
              labelId="document-category-label"
              value={form.category}
              label={t('documents.categoryLabel')}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
            >
              {CATEGORY_KEYS.map((category) => (
                <MenuItem key={category} value={category}>
                  {t(`documents.category.${category}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            multiline
            rows={2}
            margin="normal"
            label={t('documents.notes')}
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />

          <TextField
            fullWidth
            type="date"
            margin="normal"
            label={t('documents.expiresOn')}
            InputLabelProps={{ shrink: true }}
            helperText={t('documents.expiresOnHint')}
            value={form.expiresOn}
            onChange={(e) => setForm({ ...form, expiresOn: e.target.value })}
          />

          {(!showAudienceTabs || form.audience === 'shared') && (
            <>
              <FormControlLabel
                sx={{ mt: 1 }}
                control={
                  <Switch
                    checked={form.shareWithTrip}
                    onChange={(e) => setForm({ ...form, shareWithTrip: e.target.checked })}
                  />
                }
                label={t('documents.shareWithTripLabel')}
              />
              <Typography sx={{ fontSize: 12.5, color: DOCUMENT.ink3 }}>
                {form.shareWithTrip ? t('documents.shareOnHint') : t('documents.shareOffHint')}
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={uploading || !form.file}
            sx={{
              bgcolor: DOCUMENT.oxblood,
              color: DOCUMENT.ivory,
              borderRadius: 0,
              textTransform: 'uppercase',
              letterSpacing: '0.09em',
              boxShadow: 'none',
              '&:hover': { bgcolor: DOCUMENT.nav, boxShadow: 'none' },
            }}
          >
            {uploading ? t('documents.uploading') : t('documents.upload')}
          </Button>
        </DialogActions>
      </Dialog>

      <DocumentPreviewDialog
        preview={preview}
        onClose={closePreview}
        onDownload={handlePreviewDownload}
      />
    </Box>
  );
};

export default TripDocuments;
